import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";

/**
 * Pantalla bloqueante: el usuario tiene una contraseña temporal
 * (must_change_password) y debe crear una nueva antes de continuar.
 */
export default function ForcePasswordChange() {
  const { refresh, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd1.length < 8) { setError(t("auth_recovery.min_length")); return; }
    if (pwd1 !== pwd2) { setError(t("auth_recovery.passwords_no_match")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: pwd1,
      });
      if (data?.token) localStorage.setItem("casarent_token", data.token);
      toast.success(t("profile.password_changed"));
      await refresh(); // limpia must_change_password → desbloquea la app
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[color:var(--page-bg)] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md" data-testid="force-password-change">
        <div className="mb-6 text-center">
          <div className="w-12 h-12 rounded-[14px] mx-auto mb-3 flex items-center justify-center text-white" style={{ background: "var(--brand-fill)" }}>
            <KeyRound className="w-6 h-6" />
          </div>
          <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-tight text-stone-900">
            {t("auth_recovery.force_title")}
          </h1>
          <p className="text-stone-500 mt-1.5">{t("auth_recovery.force_subtitle")}</p>
        </div>

        <form onSubmit={onSubmit} className="card-base space-y-4" data-testid="force-password-form">
          <div>
            <label className="field-label">{t("auth_recovery.temp_password")}</label>
            <input type="password" required className="field" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="force-current-input" />
          </div>
          <div>
            <label className="field-label">{t("auth_recovery.new_password")}</label>
            <input type="password" required minLength={8} className="field" autoComplete="new-password"
              value={pwd1} onChange={(e) => setPwd1(e.target.value)} data-testid="force-new-input" />
            <p className="text-xs text-stone-400 mt-1">{t("auth_recovery.min_length")}</p>
          </div>
          <div>
            <label className="field-label">{t("auth_recovery.confirm_password")}</label>
            <input type="password" required minLength={8} className="field" autoComplete="new-password"
              value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="force-confirm-input" />
          </div>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 rounded-[10px] px-3 py-2" data-testid="force-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="force-submit-button">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("auth_recovery.save_continue")}
          </button>
        </form>

        <button onClick={doLogout} className="mt-4 w-full text-sm text-stone-500 hover:text-stone-700 flex items-center justify-center gap-1" data-testid="force-logout-btn">
          <LogOut className="w-3.5 h-3.5" /> {t("common.logout")}
        </button>
      </div>
    </div>
  );
}
